import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';

type HelpGuide = 'user' | 'developer';

function GuideSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="help-guide-section" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`}>{title}</h3>
      <div className="help-guide-copy">{children}</div>
    </section>
  );
}

function HelpGuideTabs() {
  return (
    <nav className="help-guide-tabs" aria-label="Help guides">
      <NavLink end to="/help">
        User guide
      </NavLink>
      <NavLink to="/help/developer">Developer guide</NavLink>
    </nav>
  );
}

function UserGuide() {
  return (
    <section className="help-guide" aria-labelledby="user-guide-title">
      <div className="help-guide-heading">
        <div>
          <p className="help-kicker">User guide</p>
          <h2 id="user-guide-title">Work with inventory</h2>
        </div>
        <Link to="/inventory" className="button-link primary-link">
          Open inventory
        </Link>
      </div>

      <div className="help-guide-grid help-guide-grid-user">
        <GuideSection id="find-items" title="Find items">
          <ol className="help-steps">
            <li>Enter one Item ID in the top search to open that item directly.</li>
            <li>
              Enter several IDs separated by commas or spaces to open Inventory with those items
              filtered.
            </li>
            <li>
              Use the table filters when you need to narrow by customer, site, part type, or status.
            </li>
          </ol>
        </GuideSection>

        <GuideSection id="filter-sort" title="Filter and sort">
          <p>
            Applied filters stay beneath the controls so the table position does not shift. Remove a
            filter from that row when you no longer need it.
          </p>
          <p>
            Select a table heading once for ascending order, again for descending order, and a third
            time to restore the default order.
          </p>
        </GuideSection>

        <GuideSection id="read-context" title="Read operational context">
          <dl className="help-definition-list">
            <div>
              <dt>Assigned site</dt>
              <dd>The site accountable for the item.</dd>
            </div>
            <div>
              <dt>Current location</dt>
              <dd>Where the item is physically located now.</dd>
            </div>
            <div>
              <dt>Attention</dt>
              <dd>An issue that needs follow-up, independent of lifecycle status.</dd>
            </div>
          </dl>
        </GuideSection>

        <GuideSection id="attention" title="Handle attention flags">
          <p>
            Status describes lifecycle. Attention marks follow-up work. The Exceptions view can show
            flags raised automatically, manually, or both.
          </p>
          <p>Check the flag source and item context before clearing an issue or changing status.</p>
          <Link to="/inventory?attention=true" className="help-inline-link">
            Open exceptions
          </Link>
        </GuideSection>

        <GuideSection id="notifications" title="Review device notifications">
          <p>
            The weekly device summary finds devices at customer sites that went quiet or were not
            installed. It recommends asking the site to install, return, or check a device;
            uncertain cases stay internal.
          </p>
          <ol className="help-steps">
            <li>
              On <strong>This week</strong>, review who would be told and filter by decision,
              message, device type, or customer.
            </li>
            <li>
              Check <strong>Left out</strong> for devices already covered by a return, field work,
              or snooze. <strong>Not due</strong> devices are waiting for their next reminder.
            </li>
            <li>
              Snooze a device only when someone is handling it. Choose an end date and record a
              useful reason; the reason is also added to the site status log.
            </li>
          </ol>
          <p>
            The scheduled summary normally runs weekly. Leave <strong>Watch-only until</strong>{' '}
            empty to prevent email. When enabled, email goes only to the customer&apos;s Tidal
            owners; customer contacts are not emailed yet.
          </p>
          <Link to="/notifications" className="help-inline-link">
            Open notifications
          </Link>
        </GuideSection>

        <GuideSection id="update" title="Make reviewed updates">
          <p>
            Open an item to review its details and history before editing. For a group of items,
            select rows in Inventory. Use <strong>Shift-click</strong> to select a visible range.
          </p>
          <p>
            Choose <strong>Edit selected</strong> or <strong>Record maintenance</strong>, then
            review the change before applying it.
          </p>
        </GuideSection>

        <GuideSection id="workspace" title="Use the right workspace">
          <p>
            Parts catalog contains reusable part definitions. Inventory contains the serialized
            items built from those definitions.
          </p>
          <p>
            Customers organize sites by <strong>country</strong> and <strong>region</strong> so site
            allocation remains easy to scan.
          </p>
        </GuideSection>
      </div>
    </section>
  );
}

function DeveloperGuide() {
  return (
    <section className="help-guide" aria-labelledby="developer-guide-title">
      <div className="help-guide-heading">
        <div>
          <p className="help-kicker">Developer guide</p>
          <h2 id="developer-guide-title">Build the inventory app</h2>
        </div>
      </div>

      <div className="help-guide-grid">
        <GuideSection id="mock-mode" title="Run mock mode">
          <p>
            Start the app in mock mode for safe, repeatable interface and workflow testing. It does
            not write to production inventory.
          </p>
          <pre className="help-code-block">
            <code>{`VITE_API_MODE=mock pnpm dev`}</code>
          </pre>
          <p>Use the configured local port when testing through the development tunnel.</p>
        </GuideSection>

        <GuideSection id="architecture" title="Follow the application layers">
          <ul className="help-list">
            <li>
              <strong>Pages</strong> compose routes and workspace layout.
            </li>
            <li>
              <strong>Features</strong> own focused workflow behavior.
            </li>
            <li>
              <strong>API adapters</strong> are the only data-access boundary.
            </li>
            <li>
              <strong>Domain modules</strong> hold pure inventory rules.
            </li>
          </ul>
          <p>
            Do not import mock data into UI components or bypass <code>InventoryApi</code>.
          </p>
        </GuideSection>

        <GuideSection id="integrity" title="Preserve inventory integrity">
          <ul className="help-list">
            <li>Keep assigned site and current location as independent fields.</li>
            <li>Include the current item version in every update.</li>
            <li>Update attention and its reason together.</li>
            <li>Preserve backend values and translate them only for display.</li>
            <li>Do not introduce lifecycle restrictions in the interface.</li>
          </ul>
        </GuideSection>

        <GuideSection id="verify" title="Verify before commit">
          <p>Run the full local check suite before handing off a change.</p>
          <pre className="help-code-block">
            <code>{`pnpm typecheck\npnpm lint\npnpm test\npnpm test:e2e\npnpm build\npnpm validate:repo`}</code>
          </pre>
          <p>
            For interface changes, also check keyboard access, both themes, responsive layout, and
            reduced motion.
          </p>
        </GuideSection>
      </div>
    </section>
  );
}

export function HelpPage({ guide = 'user' }: { guide?: HelpGuide }) {
  const isUserGuide = guide === 'user';

  return (
    <div className="help-page">
      <PageHeader
        title="Help & guides"
        description={
          isUserGuide
            ? 'Find, review, and update serialized inventory.'
            : 'Build, test, and maintain the inventory application.'
        }
      />

      <HelpGuideTabs />
      {isUserGuide ? <UserGuide /> : <DeveloperGuide />}
    </div>
  );
}
